<template>
  <div>
    <LfBlueTitleComponent :title="TEXT.title" />
    <div class="step-wizard-container">
      <h2 class="step-title">{{ TEXT.importFinished }}</h2>
      <div class="statistics-container">
        <div class="statistics-row">
          <div class="statistic-card statistic-card--success">
            <div class="statistic-content">
              <div class="statistic-label">{{ TEXT.createdRecords }}</div>
              <div class="statistic-number">{{ importExpedientsProcessStore.importSummary?.successCount ?? 0 }}</div>
            </div>
          </div>
          <div class="statistic-card statistic-card--error">
            <div class="statistic-content">
              <div class="statistic-label">{{ TEXT.errorRecords }}</div>
              <div class="statistic-number">{{ importExpedientsProcessStore.importSummary?.failureCount ?? 0 }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showFailureLink" class="info-message">
        <p class="info-text">
          {{ TEXT.failedInfo }}
        </p>
        <a href="#" class="download-link" @click.prevent="onExecuteAction(ActionName.DOWNLOAD)">
          {{ TEXT.downloadFailedRecords }}
        </a>
      </div>
      <div v-else-if="showTecnicalError" class="info-message">
        <div class="error-alert-container">
          <img
            src="@/assets/images/svg/icon-close-rounded.svg"
            class="error-icon"
            alt="Error icon"
          />
          <div class="error-message">
            {{ TEXT.technicalError }}
          </div>
        </div>
      </div>
      <div class="dialog-actions-container">
        <LfActionsBarComponent
          :buttons="buttons"
          @execute-action="onExecuteAction"
          class="action-buttons"
          :showKebabMenu="false"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LfBlueTitleComponent from '@/components/HeaderTitle/LfBlueTitleComponent.vue'
import { useI18n } from 'vue-i18n-composable'
import LfActionsBarComponent from '@/components/ActionsBar/LfActionsBarComponent.vue'
import { computed, onMounted } from 'vue'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import { ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import { inject } from 'vue'
import { createLink } from '@/helpers/blob'
import {
  useImportExpedientsProcessStore
} from '@/views/Expedients/components/ImportExpedientsDialog/stores/useImportExpedientsProcessStore'

const closeDialog = inject('closeDialog') as () => void
const resetStep = inject('resetStep') as () => void

// @note: no se desestructura para mantener la reactividad y el estado definido en el componente padre
const importExpedientsProcessStore = useImportExpedientsProcessStore()

const { t } = useI18n()
const TEXT = {
  title: t('components.expedient_import_dialog.title').toString(),
  importFinished: t('components.expedient_import_dialog.summary.import_finished').toString(),
  createdRecords: t('components.expedient_import_dialog.summary.created_records').toString(),
  errorRecords: t('components.expedient_import_dialog.summary.error_records').toString(),
  failedInfo: t('components.expedient_import_dialog.summary.failed_info').toString(),
  close: t('action_buttons.close').toString(),
  newImport: t('action_buttons.new_import').toString(),
  downloadFailedRecords: t('components.expedient_import_dialog.summary.download_failed_records').toString(),
  failureFileName: t('components.expedient_import_dialog.summary.failure_file_name').toString(),
  technicalError: t('components.expedient_import_dialog.summary.technical_error').toString(),
}
const buttons = computed(() => {
  return [
    {
      tooltip: TEXT.newImport,
      variant: LabelButtonVariant.OUTLINED,
      action: ActionName.NEW,
      enabled: true
    },
    {
      tooltip: TEXT.close,
      variant: LabelButtonVariant.SOLID,
      action: ActionName.CLOSE,
      enabled: true
    }
  ]
})

const showFailureLink = computed(():boolean => {
  return importExpedientsProcessStore.hasFailures
})

const showTecnicalError = computed(():boolean => {
  return importExpedientsProcessStore.hasSummaryError
})

function onExecuteAction(action: string ) {
  switch (action) {
    case ActionName.NEW:
      importExpedientsProcessStore.init()
      resetStep()
      return
    case ActionName.CLOSE:
      closeDialog()
      return
    case ActionName.DOWNLOAD:
      downloadFailureImportExpedientFile()
      return
  }
}

// Función para descargar fichero de errores
async function downloadFailureImportExpedientFile() {
  try {
    const failureFail = await importExpedientsProcessStore.downloadFailureImportExpedientsFile()
    createLink(failureFail.downloadUrl, TEXT.failureFileName)
  } catch (error) {
    console.error('ERROR download failure file', error)
  }
}
</script>

<style lang="scss" scoped>
.step-wizard-container {
  width: 100%;

  .step-title {
    @include inter-medium-14;
    color: $main-1000;
  }

  .step-instructions {
    @include inter-medium-14;
    color: $neutral-grey-800;
    margin-top: $spacing-xs;
  }

  .static-alert {
    margin-top: $spacing-md;
  }
}

.statistics-container {
  margin-top: $spacing-lg;
  margin-bottom: $spacing-lg;
}

.statistics-row {
  @include flex($justify-content: space-between);
  gap: $spacing-lg;
}

.statistic-card {
  @include flex($justify-content: center);
  flex: 1;
  border-radius: 8px;
  padding: $spacing-md;
  min-height: 120px;

  &--success {
    background-color: $main-050;
    border: 1px solid $corporate-color;
  }

  &--error {
    background-color: $white-01;
    border: 1px solid $neutral-grey-200;
  }
}

.statistic-content {
  @include flex($flex-direction: column, $align-items: center, $justify-content: center);
  text-align: center;
}

.statistic-label {
  @include inter-regular-14;
  color: $neutral-grey-700;
  margin-bottom: $spacing-xs;
}

.statistic-number {
  @include inter-bold-28;
  color: $main-1000;
}

.info-message {
  margin-bottom: $spacing-lg;
  padding: 0;
  background-color: transparent;
}

.error-alert-container {
  display: flex;
  align-items: center;
  justify-content: left !important;
  width: 100%;
  padding: $spacing-xxs $spacing-lg;
  gap: $spacing-xs;
  border-radius: $cornerRadius-sm;
  background-color: $tertiary-persianRed-050;

  .error-icon {
    height: 20px;
    width: 20px;
    flex-shrink: 0;
    // Aplicar el color $tertiary-persianRed-200 (#D12D30) usando filter
    filter: brightness(0) saturate(100%) invert(23%) sepia(89%) saturate(2851%) hue-rotate(344deg) brightness(94%) contrast(94%);
  }

  .error-message {
    @include inter-regular-12;
    color: $neutral-grey-800;
    width: 97%;
  }
}

.info-text {
  @include inter-regular-14;
  color: $neutral-grey-800;
  margin: 0 0 $spacing-sm 0;
  line-height: 1.4;
}

.download-link {
  @include inter-regular-14;
  color: $corporate-color;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: darken($corporate-color, 10%);
  }
}
.dialog-actions-container {
  @include flex($justify-content: flex-end);
  width: 100%;
  margin-top: $spacing-xl;

  ::v-deep .main-action-button {
    @include main-action-button--rectangle;
  }

  ::v-deep .secondary-action-button {
    @include secondary-action-button--rectangle;
  }
}
</style>
